/**
 * §F.2 22h (DCTA-170, F35): the figure the customer is typing in the amount sheet, in words — by spoken.md §2.1's rules,
 * the same the bank speaks by (lab/dcta `tts/say.ts`): whole dollars British style ("one thousand and five", "five
 * hundred and fifty"), the cents after them ("and twenty cents"), "one Singapore dollar", under a dollar "fifty cents".
 * The sheet's figure is always Singapore dollars (SGD is fixed in the field), so the currency is named once, here.
 * Integer arithmetic on the typed digits only; nothing is rounded. Not a figure (or nothing above zero): "".
 */
const ONES = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"] as const;
const TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"] as const;

function under100(n: number): string {
  if (n < 20) return ONES[n] ?? "";
  return `${TENS[Math.floor(n / 10)] ?? ""}${n % 10 === 0 ? "" : `-${ONES[n % 10] ?? ""}`}`;
}

function under1000(n: number): string {
  const h = Math.floor(n / 100);
  const r = n % 100;
  if (h === 0) return under100(r);
  return `${ONES[h] ?? ""} hundred${r === 0 ? "" : ` and ${under100(r)}`}`;
}

const GROUPS: readonly (readonly [bigint, string])[] = [
  [1_000_000_000n, "billion"],
  [1_000_000n, "million"],
  [1_000n, "thousand"],
];

/** A whole number in words, British style (1,005 → "one thousand and five"). */
function wholeWords(value: bigint): string {
  let n = value;
  const parts: string[] = [];
  for (const [size, name] of GROUPS) {
    const g = n / size;
    if (g > 0n) parts.push(`${wholeWords(g)} ${name}`);
    n %= size;
  }
  const rest = Number(n);
  if (rest > 0) parts.push(parts.length > 0 && rest < 100 ? `and ${under100(rest)}` : under1000(rest));
  return parts.join(" ");
}

/** "800", "800.", "80.2", "50,000", ".5" → the amount in words; anything else (or zero) → "". */
export function sgdWords(typed: string): string {
  const m = /^(\d{1,3}(?:,\d{3})+|\d*)(?:\.(\d{0,2}))?$/.exec(typed.trim());
  if (m === null) return "";
  const dollars = BigInt((m[1] ?? "").replace(/,/g, "") || "0");
  const cents = Number((m[2] ?? "").padEnd(2, "0") || "0");
  if (dollars === 0n && cents === 0) return "";
  const centWords = cents === 0 ? "" : `${under100(cents)} ${cents === 1 ? "cent" : "cents"}`;
  const text =
    dollars === 0n
      ? centWords
      : `${wholeWords(dollars)} ${dollars === 1n ? "Singapore dollar" : "Singapore dollars"}${centWords === "" ? "" : ` and ${centWords}`}`;
  return text.charAt(0).toUpperCase() + text.slice(1);
}
